// Constantes compartidas de tipos de documento.
// Antes vivían sólo en DocumentosPage.tsx; ahora también las usa el modal
// "Subir documento" dentro de la respuesta de observación.

// Tipos de documento que SOLO admiten planilla de cálculo (Excel/ODS), sea por la
// vía que sea (Subir documentos, wizard, respuesta a una observación…):
//   - LISTADO_ADHERENTES_EXCEL: Art. 19 del reconocimiento, se procesa en el
//     control de adherentes.
//   - PADRON_AFILIADOS: el padrón del reempadronamiento. El certificado firmado es
//     un tipo aparte (PADRON_AFILIADOS_PDF), así que este siempre es la planilla.
// El backend valida lo mismo (TIPOS_SOLO_EXCEL en config/documentosObligatorios.ts).
export const TIPOS_SOLO_EXCEL = ['LISTADO_ADHERENTES_EXCEL', 'PADRON_AFILIADOS'];
export const ACCEPT_EXCEL_ODS =
  '.xlsx,.xls,.ods,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.oasis.opendocument.spreadsheet';
export const ACCEPT_DOC_DEFAULT = '.pdf,.jpg,.jpeg,.png';

// Mensaje de error cuando un tipo que exige planilla recibe otra cosa. Usa la
// etiqueta legible del tipo, así sirve para cualquiera de TIPOS_SOLO_EXCEL.
export function msgSoloExcel(tipo?: string): string {
  const label = (tipo && tipoDocLabels[tipo]) || 'Este documento';
  return `${label} debe ser un archivo Excel (.xlsx/.xls) u ODS.`;
}

// `accept` del input file según el tipo de documento seleccionado.
export function acceptParaTipo(tipo?: string): string {
  return tipo && TIPOS_SOLO_EXCEL.includes(tipo) ? ACCEPT_EXCEL_ODS : ACCEPT_DOC_DEFAULT;
}

// Valida (por extensión) que un archivo sea Excel/ODS.
export function esArchivoExcelOds(nombre: string): boolean {
  const n = nombre.toLowerCase();
  return n.endsWith('.xlsx') || n.endsWith('.xls') || n.endsWith('.ods');
}

export const TIPOS_PARTIDO = [
  'RESOLUCION_RECONOCIMIENTO', 'CARTA_ORGANICA', 'BASES_ACCION_POLITICA',
  'ACTA_ASAMBLEA', 'CERTIFICADO_AUTORIDADES', 'CONSTANCIA_CUENTA_BANCARIA',
  'PADRON_AFILIADOS', 'PADRON_AFILIADOS_PDF', 'INFORME_FINANCIERO',
  'RESOLUCION_JUSTICIA_ELECTORAL', 'CONSTANCIA_VIGENCIA_JUZGADO',
  'ACUERDO_CONSTITUTIVO', 'ACUERDO_FUNCIONAMIENTO', 'ACUERDO_FINANCIERO',
  'CONSTANCIA_VIGENCIA_PARTIDOS', 'CONFORMIDAD_PARTIDOS',
  'CONSTANCIA_REEMPADRONAMIENTO', 'ACTA_CONSTITUTIVA',
  'PLATAFORMA_ELECTORAL', 'DECLARACION_PRINCIPIOS', 'SIMBOLO',
  // Proceso electoral interno (etapa de autoridades definitivas del reconocimiento).
  'PUBLICACION_LLAMADO_ELECCIONES', 'CRONOGRAMA_ELECCIONES',
];

export const TIPOS_AUTORIDADES = ['DNI_FRENTE_AUTORIDAD', 'DNI_DORSO_AUTORIDAD'];

export const TIPOS_APODERADOS = [
  'DNI_FRENTE_APODERADO', 'DNI_DORSO_APODERADO',
  'ACTA_DESIGNACION_APODERADOS',
];

export const TIPOS_OTROS = ['RESOLUCION', 'NOTA', 'DICTAMEN', 'COMPROBANTE', 'BALANCE', 'OTRO'];

// Documentación TEP: todos los PDFs del circuito SEP → Secretario → TEP, en orden
// cronológico del flujo. Los genera la SEP (publicación de atributos, informe al
// Secretario, informe al TEP), el Secretario (devolución a la SEP) o se cargan
// (resolución del TEP). Van en su propia sección "Resoluciones", NO en
// "Otros documentos". Aplica a TODOS los tipos de expediente, no solo
// reconocimiento: reempadronamiento y el circuito de no presentado generan los
// mismos tipos de documento.
//
// La sección se llamaba "Documentación TEP". El nombre dejó de servir cuando entró
// la constitución de alianzas: ahí el circuito lo resuelve el Secretario Electoral y
// el Tribunal no interviene, así que el título anunciaba un órgano que no participa.
// "Resoluciones" describe lo que hay adentro en todos los trámites.
export const TIPOS_RESOLUCIONES = [
  'PUBLICACION_ATRIBUTOS',
  'INFORME_SECRETARIO',
  'INFORME_TEP',
  'INFORME_DEVOLUCION_SECRETARIO',
  'RESOLUCION_TEP',
  // Resolución con la que el SECRETARIO ELECTORAL resuelve el fondo del trámite,
  // sin intervención del Tribunal (reempadronamiento aprobado por él).
  'RESOLUCION_SECRETARIO',
  // Constitución de alianza: el circuito lo resuelve el Secretario, no el TEP, pero
  // sus piezas son del mismo tipo (informe de elevación + resolución) y van a la
  // tab Informes, no a "Otros documentos".
  'INFORME_ELEVACION_ALIANZA',
  'RESOLUCION_RECONOCIMIENTO_ALIANZA',
];

// =============================================================================
// Documentos del expediente de CONSTITUCIÓN DE ALIANZA (Art. 27, Ley XII N° 23)
// =============================================================================
// Lista PROPIA por tipo de trámite, igual que DOCS_OBLIGATORIOS_RECONOCIMIENTO.
//
// No se resuelve con una clave 'ALIANZA' en DOCS_OBLIGATORIOS_EXTRA: ese mapa SUMA
// sobre DOCS_OBLIGATORIOS_BASE, que pide RESOLUCION_RECONOCIMIENTO, CARTA_ORGANICA,
// BASES_ACCION_POLITICA y PADRON_AFILIADOS. Una alianza no tiene nada de eso, así
// que reclamárselos la dejaría incompleta para siempre.
//
// Espeja DOCS_OBLIGATORIOS_CONSTITUCION_ALIANZA del backend
// (config/documentosObligatorios.ts): si se toca una, hay que tocar la otra.
export const DOCS_OBLIGATORIOS_ALIANZA = [
  'ACTA_VOLUNTAD_ALIARSE',                // Art. 27 a — uno por integrante
  // Los cuatro son tipos COMPARTIDOS con los demás trámites: la ley nombra igual al
  // mismo documento en el Art. 24 d) (confederación) y en el Art. 27 c/d) (alianza).
  'PLATAFORMA_ELECTORAL',                 // Art. 27 c
  'ACUERDO_CONSTITUTIVO',                 // Art. 27 d
  'ACUERDO_FUNCIONAMIENTO',               // Art. 27 d
  'ACUERDO_FINANCIERO',                   // Art. 27 d
  'REGLAMENTO_ELECTORAL_ALIANZA',         // Art. 27 e
];

// Complementarios: se listan, pero NO son del Art. 27 (salen del Art. 24, de
// confederaciones), así que no bloquean la elevación por incumplimiento de ese
// artículo. Van bajo un subtítulo aparte, sin cita de artículo.
export const DOCS_COMPLEMENTARIOS_ALIANZA = [
  'ACTA_DESIGNACION_APODERADOS',          // uno por apoderado
  'ACTA_DESIGNACION_ORGANO_ALIANZA',      // uno por órgano
  // Emblema y símbolos: el Art. 27 no los pide como documentación (son un ATRIBUTO),
  // pero hay que revisarlos igual, así que tienen sección con su propio check.
  'SIMBOLO',
  // DNI de las personas del expediente: dos tipos por sección (frente y dorso) y N
  // documentos por tipo. Tampoco los pide el Art. 27, así que no bloquean.
  'DNI_FRENTE_AUTORIDAD',
  'DNI_DORSO_AUTORIDAD',
  'DNI_FRENTE_APODERADO',
  'DNI_DORSO_APODERADO',
];

/**
 * Secciones de DNI: UN check que abarca los dos tipos. La supervisora decide una
 * sola cosa —"los DNI de las autoridades están"—, no dos; tildar frentes y dorsos por
 * separado no corresponde a ninguna decisión real.
 *
 * La tabla del backend sigue teniendo una fila por tipo (no hay columna de sección):
 * el grupo se expande al escribir. Espeja GRUPOS_TIPOS_DOCUMENTO del backend.
 */
export const GRUPOS_TIPOS_DOCUMENTO: Record<string, string[]> = {
  GRUPO_DNI_AUTORIDADES: ['DNI_FRENTE_AUTORIDAD', 'DNI_DORSO_AUTORIDAD'],
  GRUPO_DNI_APODERADOS: ['DNI_FRENTE_APODERADO', 'DNI_DORSO_APODERADO'],
  // Para RECONOCIMIENTO: ahí quien presenta todavía no es apoderado, es iniciador.
  GRUPO_DNI_INICIADORES: ['DNI_FRENTE_INICIADOR', 'DNI_DORSO_INICIADOR'],
};

/**
 * Tipos que se muestran en su PROPIA sección de /documentos y por eso NO entran en
 * el checklist de "Documentos Obligatorios": estarían dos veces en la pantalla.
 *   - SIMBOLO → "Emblemas y símbolos"
 *   - los cuatro DNI → "Documentación de Autoridades" / "de Apoderados", que además
 *     los muestran POR PERSONA, que es lo único con lo que se pueden revisar.
 * Siguen teniendo su fila en `expediente_tipos_documento`: lo que cambia es dónde
 * se ven, no si se aprueban.
 */
export const TIPOS_CON_SECCION_PROPIA = [
  'SIMBOLO',
  ...GRUPOS_TIPOS_DOCUMENTO.GRUPO_DNI_AUTORIDADES,
  ...GRUPOS_TIPOS_DOCUMENTO.GRUPO_DNI_APODERADOS,
  ...GRUPOS_TIPOS_DOCUMENTO.GRUPO_DNI_INICIADORES,
];

/** Etiqueta de cada sección de DNI. Espeja GRUPOS_LABELS del backend. */
export const GRUPOS_LABELS: Record<string, string> = {
  GRUPO_DNI_AUTORIDADES: 'DNI de Autoridades',
  GRUPO_DNI_APODERADOS: 'DNI de Apoderados',
  GRUPO_DNI_INICIADORES: 'DNI de Iniciadores',
}
/**
 * Etiqueta con la que se nombra una SECCIÓN de documentación.
 *
 * Los cuatro tipos de DNI se colapsan en dos ("DNI de Autoridades", "DNI de
 * Apoderados"): son un requisito, no dos, y partirlos en frente y dorso repite el
 * mismo renglón. Lo usan el informe al Secretario y los carteles de la pantalla del
 * expediente, así que vive acá y no en el util de un trámite.
 */
export function etiquetaSeccion(tipo: string): string {
  const grupo = Object.entries(GRUPOS_TIPOS_DOCUMENTO)
    .find(([, tipos]) => tipos.includes(tipo))?.[0];
  return grupo ? GRUPOS_LABELS[grupo] : (tipoDocLabels[tipo] || tipo);
}
;

/** Los 8 tipos del wizard de alianza. */
export const DOCS_ALIANZA_TODOS = [
  ...DOCS_OBLIGATORIOS_ALIANZA,
  ...DOCS_COMPLEMENTARIOS_ALIANZA,
];

/** ¿El tipo es del Art. 27 (bloqueante) o complementario? */
export function esComplementarioAlianza(tipo: string): boolean {
  return DOCS_COMPLEMENTARIOS_ALIANZA.includes(tipo);
}

// Motivo EXACTO con el que el backend (reconocimiento.ts) marca RECHAZADO los
// símbolos cuando el TEP rechaza los atributos. Se filtra de la observación/
// intimación automática (no es una objeción a ese símbolo puntual). Debe coincidir
// carácter por carácter con MOTIVO_RECHAZO_TEP_SIMBOLO del backend.
export const MOTIVO_RECHAZO_TEP_SIMBOLO = 'El TEP rechazó los atributos';

// Documentos obligatorios del expediente de reempadronamiento.
// BASE: requeridos a todos los tipos de partido.
// EXTRA: adicionales según el tipo de partido.
// (Fuente de verdad compartida entre DocumentosPage y el Resumen de Documentación.)
export const DOCS_OBLIGATORIOS_BASE = [
  'RESOLUCION_RECONOCIMIENTO', 'CARTA_ORGANICA', 'BASES_ACCION_POLITICA',
  'ACTA_ASAMBLEA', 'CERTIFICADO_AUTORIDADES', 'ACTA_DESIGNACION_APODERADOS',
  'CONSTANCIA_CUENTA_BANCARIA', 'PADRON_AFILIADOS', 'PADRON_AFILIADOS_PDF',
];

// DISTRITO es un valor de `Partido.tipo` (ámbito territorial); CONFEDERACION es un valor
// de `Partido.clase`. Desde que CONFEDERACION salió de `TipoPartido`, una confederación
// tiene ADEMÁS un ámbito propio, así que puede acumular las dos listas.
export const DOCS_OBLIGATORIOS_EXTRA: Record<string, string[]> = {
  DISTRITO:      ['RESOLUCION_JUSTICIA_ELECTORAL', 'CONSTANCIA_VIGENCIA_JUZGADO'],
  CONFEDERACION: ['ACUERDO_CONSTITUTIVO', 'ACUERDO_FUNCIONAMIENTO', 'CONSTANCIA_VIGENCIA_PARTIDOS', 'CONFORMIDAD_PARTIDOS'],
};

/**
 * Documentos obligatorios de un partido según su ámbito y su clase.
 * Espeja obligatoriosPorTipoPartido() del backend (config/documentosObligatorios.ts):
 * si se cambia una, hay que cambiar la otra.
 */
export function obligatoriosPorPartido(tipo?: string | null, clase?: string | null): string[] {
  const extraClase = clase && clase !== 'ESTANDAR' ? DOCS_OBLIGATORIOS_EXTRA[clase] || [] : [];
  return [
    ...DOCS_OBLIGATORIOS_BASE,
    ...(DOCS_OBLIGATORIOS_EXTRA[tipo || ''] || []),
    ...extraClase,
  ];
}

// Documentos obligatorios del expediente de RECONOCIMIENTO (Art. 18/19 - Ley XII-23).
// El listado de adherentes se recibe como adjunto; el módulo de Adherentes lo consume aparte.
// PLANILLA_ADHERENTES_FIRMAS ya NO está: los avales son las planillas firmadas de
// puño y se entregan EN PAPEL (presencialmente o a registro@electoralchubut.gob.ar),
// no se suben al sistema. Si se dejara acá, el "Resumen de Documentación" lo
// reportaría faltante para siempre. Espeja DOCS_OBLIGATORIOS_RECONOCIMIENTO del
// backend (config/documentosObligatorios.ts).
export const DOCS_OBLIGATORIOS_RECONOCIMIENTO = [
  'ACTA_CONSTITUTIVA',                        // Acta Constitutiva (Art. 18 a)
  'CARTA_ORGANICA',                           // Carta Orgánica (Art. 18 c)
  'BASES_ACCION_POLITICA',                    // Bases de Acción Política (Art. 18 c)
  'ACTA_DESIGNACION_AUTORIDADES_PROMOTORAS',  // Acta de Designación de Autoridades Promotoras (Art. 18 d)
  'LISTADO_ADHERENTES_EXCEL',                 // Listado de Adherentes (Art. 19, Excel)
];

// Documentos obligatorios de la ETAPA DE AFILIADOS del reconocimiento (provisorio
// → definitivo). Se SUMAN a los del Art. 18/19 recién cuando el trámite llega a
// esa etapa: antes no corresponde mostrarlos como faltantes, y el gate del
// backend para elevar la documentación al Secretario sigue mirando sólo el Art. 18.
//
// Es UN solo documento: el Excel que procesa el sistema. El "Padrón Certificado
// (PDF firmado)" ya NO se pide acá — el padrón definitivo lo emite la Secretaría
// Electoral hacia el partido, no al revés. Lo que el partido entrega en papel son las
// fichas de afiliación del Art. 17, que no se cargan como documento del expediente
// (se cotejan una por una en el módulo de Afiliados).
// Espeja DOCS_AFILIADOS_DEFINITIVO del backend (routes/reconocimiento.ts).
export const DOCS_OBLIGATORIOS_RECONOCIMIENTO_AFILIADOS = [
  'PADRON_AFILIADOS',      // Padrón de Afiliados (Excel, sin firma)
];

// Documentos obligatorios de la ETAPA DE AUTORIDADES DEFINITIVAS del reconocimiento
// (elecciones internas). Se suman a los del Art. 18/19 recién cuando el trámite
// llega a esa etapa. No alcanza con el RESULTADO de la elección: también hay que
// acreditar cómo se convocó (publicación del llamado + cronograma).
// La Resolución de la Proclamación de Autoridades se guarda como CERTIFICADO_AUTORIDADES.
// Espeja DOCS_AUTORIDADES_DEFINITIVO del backend (routes/reconocimiento.ts).
export const DOCS_OBLIGATORIOS_RECONOCIMIENTO_AUTORIDADES = [
  'PUBLICACION_LLAMADO_ELECCIONES', // Publicación de llamado a elecciones
  'CRONOGRAMA_ELECCIONES',          // Cronograma de elecciones
  'ACTA_ASAMBLEA',                  // Acta de asamblea donde se proclamaron las autoridades
  'CERTIFICADO_AUTORIDADES',        // Resolución de la Proclamación de Autoridades
  'ACTA_DESIGNACION_APODERADOS',    // Acta de Designación de Apoderados
];

// Orden canónico de columnas de obligatorios para el resumen (unión de todos los tipos).
export const DOCS_OBLIGATORIOS_TODOS = [
  ...DOCS_OBLIGATORIOS_BASE,
  ...DOCS_OBLIGATORIOS_EXTRA.DISTRITO,
  ...DOCS_OBLIGATORIOS_EXTRA.CONFEDERACION,
];

export const TIPOS_DNI_PERSONA = ['DNI_FRENTE_AUTORIDAD', 'DNI_DORSO_AUTORIDAD', 'DNI_FRENTE_APODERADO', 'DNI_DORSO_APODERADO'];

/**
 * Tipos del trámite de CONSTITUCIÓN DE ALIANZA. Van en `TODOS_LOS_TIPOS` para que
 * aparezcan al editar el tipo de un documento y al subir uno a mano: sin esto un
 * documento de alianza no se podía reclasificar (el selector no ofrecía su tipo).
 */
export const TIPOS_ALIANZA = [
  ...DOCS_ALIANZA_TODOS,
  'ACTA_MODIFICACION_ALIANZA',
  'RESOLUCION_RECONOCIMIENTO_ALIANZA',
  'RESOLUCION_MODIFICACION_ALIANZA',
  'INFORME_ELEVACION_ALIANZA',
];

/** Tipos de CANDIDATURAS, por el mismo motivo. */
export const TIPOS_CANDIDATURA = [
  'ACEPTACION_CANDIDATURA', 'ANTECEDENTES_PENALES_CANDIDATO',
  'PLATAFORMA_ELECTORAL_CANDIDATURA', 'ACTA_PROCLAMACION',
  'DNI_FRENTE_CANDIDATO', 'DNI_DORSO_CANDIDATO', 'FOTO_CANDIDATO',
  'COMPROBANTE_RESIDENCIA',
];

export const TODOS_LOS_TIPOS = Array.from(new Set([
  ...TIPOS_PARTIDO, ...TIPOS_AUTORIDADES, ...TIPOS_APODERADOS,
  ...TIPOS_ALIANZA, ...TIPOS_CANDIDATURA,
  // Los del circuito Secretario → TEP: se generan solos, pero hay que poder
  // reclasificar uno mal cargado.
  ...TIPOS_RESOLUCIONES,
  ...TIPOS_OTROS,
]));

/**
 * Tipos que puede adjuntar una agrupación (o la SEP en su nombre) al responder una
 * observación: todos menos los que genera la Secretaría (TIPOS_RESOLUCIONES: informes
 * al Secretario y al TEP, resoluciones). TODOS_LOS_TIPOS no se toca: los necesita
 * Documentos para reclasificar un documento mal cargado.
 */
export const TIPOS_RESPUESTA_AGRUPACION = TODOS_LOS_TIPOS.filter((t) => !TIPOS_RESOLUCIONES.includes(t));

export const tipoDocLabels: Record<string, string> = {
  // --- Candidaturas ---
  ACEPTACION_CANDIDATURA: 'Aceptación de candidatura',
  ANTECEDENTES_PENALES_CANDIDATO: 'Antecedentes penales del candidato',
  PLATAFORMA_ELECTORAL_CANDIDATURA: 'Plataforma electoral (candidatura)',
  ACTA_PROCLAMACION: 'Acta de proclamación',
  DNI_FRENTE_CANDIDATO: 'DNI del candidato (frente)',
  DNI_DORSO_CANDIDATO: 'DNI del candidato (dorso)',
  FOTO_CANDIDATO: 'Foto del candidato',
  COMPROBANTE_RESIDENCIA: 'Comprobante de residencia',

  // --- Constitución de alianza (Arts. 26-27) ---
  // El Art. 27 a) pide CONSTANCIA, no acta. Quién la resolvió —el órgano competente
  // según la Carta Orgánica, no el apoderado— es lo que hay que verificar, pero va
  // en la nota de la sección y no en la etiqueta: acá el nombre entero no entraba en
  // la fila y tapaba el resto de la columna.
  ACTA_VOLUNTAD_ALIARSE: 'Constancia de la voluntad de aliarse',
  // El Art. 27 e) pide PRIMERO la constancia de la forma acordada para integrar las
  // listas; el reglamento va incluido en ella. Llamarlo sólo "Reglamento electoral"
  // invierte la jerarquía y esconde el requisito principal, que además es el
  // documento donde se reparte el poder dentro de la alianza.
  REGLAMENTO_ELECTORAL_ALIANZA: 'Forma acordada de integración de las listas y reglamento electoral',
  ACTA_DESIGNACION_ORGANO_ALIANZA: 'Acta de designación del órgano de la alianza',
  ACTA_MODIFICACION_ALIANZA: 'Acta de modificación de la alianza',
  INFORME_ELEVACION_ALIANZA: 'Informe de elevación al Secretario (Alianza)',
  RESOLUCION_RECONOCIMIENTO_ALIANZA: 'Resolución de reconocimiento de alianza',
  RESOLUCION_MODIFICACION_ALIANZA: 'Resolución de modificación de alianza',
  ACTA_CONSTITUTIVA: 'Acta Constitutiva',
  CARTA_ORGANICA: 'Carta Orgánica',
  PLATAFORMA_ELECTORAL: 'Plataforma electoral',
  DECLARACION_PRINCIPIOS: 'Declaración de Principios',
  ACTA_ASAMBLEA: 'Acta de asamblea donde se proclamaron las autoridades',
  PUBLICACION_LLAMADO_ELECCIONES: 'Publicación de llamado a elecciones',
  CRONOGRAMA_ELECCIONES: 'Cronograma de elecciones',
  PADRON_AFILIADOS: 'Padrón de Afiliados (excel)',
  PADRON_AFILIADOS_PDF: 'Padrón Certificado',
  BALANCE: 'Balance',
  RESOLUCION: 'Resolución',
  NOTA: 'Nota',
  DICTAMEN: 'Dictamen',
  INFORME_PERICIAL: 'Informe pericial',
  COMPROBANTE: 'Comprobante',
  OTRO: 'Otro',
  RESOLUCION_RECONOCIMIENTO: 'Acta/Resolución de Reconocimiento',
  CERTIFICADO_AUTORIDADES: 'Resolución Proclamación Autoridades',
  CONSTANCIA_CUENTA_BANCARIA: 'Constancia Cuenta Bancaria',
  INFORME_FINANCIERO: 'Informe Financiero',
  CERTIFICACION_CPCE: 'Certificación CPCECh (PDF firmado)',
  COPIA_SPECA: 'Copia SPECA (PDF firmado)',
  DNI_FRENTE_APODERADO: 'DNI (frente) Apoderado',
  DNI_DORSO_APODERADO: 'DNI (dorso) Apoderado',
  DNI_FRENTE_AUTORIDAD: 'DNI (frente) Autoridad',
  DNI_DORSO_AUTORIDAD: 'DNI (dorso) Autoridad',
  DNI_FRENTE_INICIADOR: 'DNI (frente) del Iniciador',
  DNI_DORSO_INICIADOR: 'DNI (dorso) del Iniciador',
  DNI_AUTORIDAD: 'DNI Autoridad (legado)',
  SIMBOLO: 'Emblema y símbolos',
  BASES_ACCION_POLITICA: 'Bases de Acción Política',
  ACTA_DESIGNACION_APODERADOS: 'Acta de designación de apoderados',
  ACTA_DESIGNACION_AUTORIDADES_PROMOTORAS: 'Acta de Designación de Autoridades Promotoras',
  LISTADO_ADHERENTES: 'Listado de Adherentes',
  LISTADO_ADHERENTES_EXCEL: 'Listado de Adherentes (excel)',
  DECLARACION_JURADA_ADHERENTES: 'DDJJ Autenticidad de Firmas (Adherentes)',
  PLANILLA_ADHERENTES_FIRMAS: 'Planilla de Adherentes con firmas',
  DNI_APODERADO: 'DNI Apoderado',
  RESOLUCION_JUSTICIA_ELECTORAL: 'Resolución Justicia Electoral',
  CONSTANCIA_VIGENCIA_JUZGADO: 'Constancia Vigencia Juzgado',
  // Los tres acuerdos son COMPARTIDOS: Art. 24 d) para la confederación, Art. 27 d)
  // para la alianza. La ley los nombra igual, y qué agrupación constituyen lo dice
  // el expediente. El financiero sólo lo pide el Art. 27, pero eso lo resuelve la
  // lista de obligatorios de cada trámite, no un tipo aparte.
  ACUERDO_CONSTITUTIVO: 'Acuerdo constitutivo',
  ACUERDO_FUNCIONAMIENTO: 'Acuerdo de funcionamiento',
  ACUERDO_FINANCIERO: 'Acuerdo financiero',
  CONSTANCIA_VIGENCIA_PARTIDOS: 'Constancia Vigencia Partidos',
  CONFORMIDAD_PARTIDOS: 'Conformidad Partidos Confederados',
  CONSTANCIA_REEMPADRONAMIENTO: 'Constancia de Reempadronamiento',
  PUBLICACION_ATRIBUTOS: 'Publicación de Atributos',
  INFORME_SECRETARIO: 'Informe al Secretario',
  INFORME_TEP: 'Informe al TEP',
  RESOLUCION_TEP: 'Resolución del TEP',
  RESOLUCION_SECRETARIO: 'Resolución del Secretario Electoral',
  INFORME_DEVOLUCION_SECRETARIO: 'Devolución del Secretario a la SEP',
};

// Helper para autogenerar el título de un documento DNI a partir del DNI + persona.
export function buildAutoTitleDni(tipo: string, nombre: string, apellido: string, dni: string): string {
  const labelMap: Record<string, string> = {
    DNI_FRENTE_AUTORIDAD: 'DNI Frente',
    DNI_DORSO_AUTORIDAD: 'DNI Dorso',
    DNI_FRENTE_APODERADO: 'DNI Frente',
    DNI_DORSO_APODERADO: 'DNI Dorso',
  };
  const label = labelMap[tipo] || tipoDocLabels[tipo] || tipo;
  const nombreCompleto = `${nombre} ${apellido}`.toUpperCase();
  return `${label} - ${nombreCompleto} (${dni})`;
}
